import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext } from 'react';
import { Vehicle, VehicleWithDriver } from '../models/vehicle';
import { AuthContext } from './authContext';
import * as vehicleService from '../services/vehicleService';
import type { FetchVehiclesOptions } from '../services/vehicleService';

/**
 * Interface for search truck information stored in localStorage.
 */
interface SearchTruckInfo {
  truckId: number;
  truckPlate: string;
  trailerPlate: string;
  vehicleType: string;
  name: string;
  manufacturer: string;
  model: string;
}

/**
 * Interface for Vehicle Context state and actions.
 */
interface VehicleContextType {
  vehicles: (Vehicle | VehicleWithDriver)[];
  isLoading: boolean;
  error: string | null;
  searchTrucks: SearchTruckInfo[];
  fetchVehicles: (options?: FetchVehiclesOptions) => Promise<void>;
  deleteVehicle: (id: number) => Promise<void>;
  importVehicles: () => Promise<void>;
  refreshVehicles: () => void;
  addVehicleToSearch: (vehicle: Vehicle) => void;
  removeVehicleFromSearch: (vehicleId: number) => void;
  clearSearchTrucks: () => void;
}

export const VehicleContext = createContext<VehicleContextType | undefined>(undefined);

/**
 * Props for VehicleProvider component.
 */
interface VehicleProviderProps {
  children: ReactNode;
}

/**
 * VehicleProvider component manages vehicle state across the application.
 * Provides centralized vehicle data management with CRUD operations.
 * 
 * @param {VehicleProviderProps} props - Component props containing children
 */
export const VehicleProvider: React.FC<VehicleProviderProps> = ({ children }) => {
  const [vehicles, setVehicles] = useState<(Vehicle | VehicleWithDriver)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Start with false since we don't fetch initially
  const [error, setError] = useState<string | null>(null);
  const [searchTrucks, setSearchTrucks] = useState<SearchTruckInfo[]>([]);
  
  const authContext = useContext(AuthContext);   // Get authentication state
  const isLoggedIn = authContext?.isLoggedIn || false;
  const authIsLoading = authContext?.isLoading || false;

  /**
   * Loads search trucks from localStorage on component mount.
   */
  const loadSearchTrucksFromStorage = useCallback((): SearchTruckInfo[] => {
    try {
      const existingTrucks = localStorage.getItem('searchTruckInfo');
      if (existingTrucks) {
        const trucks = JSON.parse(existingTrucks);
        // Ensure it's an array
        return Array.isArray(trucks) ? trucks : [trucks];
      }
      return [];
    } catch (_) {
      return [];
    }
  }, []);

  /**
   * Saves search trucks to localStorage.
   */
  const saveSearchTrucksToStorage = useCallback((trucks: SearchTruckInfo[]): void => {
    try {
      localStorage.setItem('searchTruckInfo', JSON.stringify(trucks));
    } catch (_) {
      // Failed to save search trucks to localStorage
    }
  }, []);

  /**
   * Adds a vehicle to the search truck list.
   * 
   * @param {Vehicle} vehicle - Vehicle to add to search
   */
  const addVehicleToSearch = useCallback((vehicle: Vehicle): void => {
    const newTruckInfo: SearchTruckInfo = {
      truckId: vehicle.id,
      truckPlate: vehicle.truckPlate || '',
      trailerPlate: vehicle.trailerPlate || '',
      vehicleType: vehicle.type || '',
      name: vehicle.name || '',
      manufacturer: vehicle.manufacturer || '',
      model: vehicle.model || ''
    };

    setSearchTrucks((prev) => {
      // Check if truck already exists
      const truckExists = prev.some(truck => truck.truckId === vehicle.id);
      if (truckExists) {
        return prev; // Don't add duplicates
      }
      
      const updatedTrucks = [...prev, newTruckInfo];
      saveSearchTrucksToStorage(updatedTrucks);
      return updatedTrucks;
    });
  }, [saveSearchTrucksToStorage]);

  /**
   * Removes a vehicle from the search truck list.
   * 
   * @param {number} vehicleId - Vehicle ID to remove from search
   */
  const removeVehicleFromSearch = useCallback((vehicleId: number): void => {
    setSearchTrucks((prev) => {
      const updatedTrucks = prev.filter(truck => truck.truckId !== vehicleId);
      saveSearchTrucksToStorage(updatedTrucks);
      return updatedTrucks;
    });
  }, [saveSearchTrucksToStorage]);

  /**
   * Clears all search trucks.
   */
  const clearSearchTrucks = useCallback((): void => {
    setSearchTrucks([]);
    saveSearchTrucksToStorage([]);
  }, [saveSearchTrucksToStorage]);

  // Load search trucks from localStorage on component mount
  useEffect(() => {
    const trucks = loadSearchTrucksFromStorage();
    setSearchTrucks(trucks);
  }, [loadSearchTrucksFromStorage]);

  // Clear search trucks when user logs out
  useEffect(() => {
    if (!isLoggedIn && !authIsLoading) {
      clearSearchTrucks();
    }
  }, [isLoggedIn, authIsLoading, clearSearchTrucks]);

  /**
   * Fetches vehicles from the API and updates the state.
   * Handles loading state and error management.
   * Only fetches if user is authenticated.
   * 
   * @param {FetchVehiclesOptions} options - Optional parameters for filtering vehicles
   */
  const fetchVehicles = useCallback(async (options: FetchVehiclesOptions = {}): Promise<void> => {
    // Don't fetch if user is not logged in
    if (!isLoggedIn) {
      setVehicles([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const result = await vehicleService.fetchVehicles(options);
      setVehicles(result);
    } catch (_) {
      const errorMessage = 'Failed to load vehicles';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  /**
   * Deletes a vehicle from the backend and updates local state.
   * Only works if user is authenticated.
   * 
   * @param {number} id - Vehicle ID to delete
   */
  const deleteVehicle = useCallback(async (id: number): Promise<void> => {
    if (!isLoggedIn) {
      throw new Error('User must be logged in to perform this action');
    }

    await vehicleService.deleteVehicle(id);
    
    // Remove vehicle from local state
    setVehicles((prev) => prev.filter((vehicle) => vehicle.id !== id));
  }, [isLoggedIn]);

  /**
   * Imports vehicles from external source (Cartrack).
   * Refreshes the vehicle list after import.
   * Only works if user is authenticated.
   */
  const importVehicles = useCallback(async (): Promise<void> => {
    if (!isLoggedIn) {
      throw new Error('User must be logged in to perform this action');
    }

    await vehicleService.importVehiclesFromCartrack();
    await fetchVehicles(); // Refresh the list after import
  }, [fetchVehicles, isLoggedIn]);

  /**
   * Manually triggers a refresh of the vehicle list.
   * Useful for components that need to force a data reload.
   */
  const refreshVehicles = useCallback((): void => {
    fetchVehicles({ with_drivers: true, with_current_booking: true, include_inactive: true });
  }, [fetchVehicles]);

  useEffect(() => {
    if (!isLoggedIn && !authIsLoading) {
      // Clear vehicles when user is not logged in
      setVehicles([]);
      setIsLoading(false);
      setError(null);
    }
  }, [isLoggedIn, authIsLoading]);

  const contextValue: VehicleContextType = {
    vehicles,
    isLoading,
    error,
    searchTrucks,
    fetchVehicles,
    deleteVehicle,
    importVehicles,
    refreshVehicles,
    addVehicleToSearch,
    removeVehicleFromSearch,
    clearSearchTrucks,
  };

  return (
    <VehicleContext.Provider value={contextValue}>
      {children}
    </VehicleContext.Provider>
  );
};

/**
 * Custom hook to use the Vehicle Context.
 * Throws an error if used outside of VehicleProvider.
 * 
 * @returns {VehicleContextType} Vehicle context value with state and actions
 */
export const useVehicleContext = (): VehicleContextType => {
  const context = React.useContext(VehicleContext);
  if (context === undefined) {
    throw new Error('useVehicleContext must be used within a VehicleProvider');
  }
  return context;
};
